// src/core/data/redux/store.ts
import { configureStore } from "@reduxjs/toolkit";
import commonSlice from "./commonSlice";
import { institutionApi } from "./apis/instituteService";
import { courseApi } from "./apis/coursesService";
import { leadApi } from "./apis/leadService";
import { institutionCourseApi } from "./apis/instituteCourseService";
import { userApi } from "./apis/userService";
import { leadAssignApi } from "./apis/leadAssignService";
import { roleApi } from "./apis/roleService";
import authSlice from "./authSlice";

import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web
import { combineReducers } from "redux";
import { followUpCommentApi } from "./apis/followUpCommentService";
import { lostReasonApi } from "./apis/lostReasonService";
import { templateApi } from "./apis/templateService";
import { businessApi } from "./apis/businessService";
import { attendanceApi } from "./apis/attendanceService";
import { invoiceApi } from "./apis/invoiceService";
import { userPermissionApi } from "./apis/userPermissionService";
import permissionSlice from "./userPermissionSlice";

// Create persist config for the auth slice
const authPersistConfig = {
  key: "auth", // Key used for localStorage
  storage, // The storage engine to use (localStorage)
};

const permissionPersistConfig = {
  key: "permission", // Key for localStorage
  storage, // Storage engine to use (localStorage)
};
// Combine reducers for persist config
const rootReducer = combineReducers({
  common: commonSlice,
  permission: persistReducer(permissionPersistConfig, permissionSlice),
  auth: persistReducer(authPersistConfig, authSlice), // Persisted auth slice
  [institutionApi.reducerPath]: institutionApi.reducer,
  [institutionCourseApi.reducerPath]: institutionCourseApi.reducer,
  [courseApi.reducerPath]: courseApi.reducer,
  [leadApi.reducerPath]: leadApi.reducer,
  [roleApi.reducerPath]: roleApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [leadAssignApi.reducerPath]: leadAssignApi.reducer,
  [followUpCommentApi.reducerPath]: followUpCommentApi.reducer,
  [lostReasonApi.reducerPath]: lostReasonApi.reducer,
  [templateApi.reducerPath]: templateApi.reducer,
  [businessApi.reducerPath]: businessApi.reducer,
  [attendanceApi.reducerPath]: attendanceApi.reducer,
  [invoiceApi.reducerPath]: invoiceApi.reducer,
  [userPermissionApi.reducerPath]: userPermissionApi.reducer,
});

// Configure the store
const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false, // Disable serializable checks for redux-persist
    })
      .concat(institutionApi.middleware)
      .concat(institutionCourseApi.middleware)
      .concat(courseApi.middleware)
      .concat(leadApi.middleware)
      .concat(roleApi.middleware)
      .concat(userApi.middleware)
      .concat(leadAssignApi.middleware)
      .concat(lostReasonApi.middleware)
      .concat(templateApi.middleware)
      .concat(followUpCommentApi.middleware)
      .concat(attendanceApi.middleware)
      .concat(businessApi.middleware)
      .concat(invoiceApi.middleware)
      .concat(userPermissionApi.middleware),
});

// Create a persistor to manage the persisted state
export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export default store;
